import type { InsightContent } from "./types";

export const categoryVsProduct: InsightContent = {
  id: "category-vs-product",
  format: "guide",
  cluster: "Ecommerce SEO",
  moneyPage: {
    es: "/servicios/ecommerce-seo",
    en: "/en/services/ecommerce-seo",
  },
  path: {
    es: "/biblioteca/ecommerce-categorias-vs-productos",
    en: "/en/library/ecommerce-category-vs-product-pages",
  },
  published: "2026-08-11",
  updated: "2026-08-11",
  readingMinutes: 9,
  es: {
    title: "Categorías vs producto: qué página posicionar en ecommerce",
    h1: "Páginas de categoría vs páginas de producto: cuál debe posicionar cada búsqueda",
    description:
      "Cómo decidir si una consulta de ecommerce debe resolverse con una página de categoría o de producto, y cómo evitar que compitan entre sí.",
    eyebrow: "Ecommerce SEO",
    summary:
      "En un catálogo grande, no toda búsqueda merece una página de producto: las consultas amplias suelen convertir mejor en una página de categoría bien estructurada, mientras que las consultas específicas de un solo artículo necesitan su propia ficha.",
    body: [
      {
        type: "p",
        text: "Uno de los errores más frecuentes en ecommerce es asumir que cada palabra clave debe ganarse con una página nueva. En la práctica, la plantilla correcta depende del tipo de intención detrás de la búsqueda, no solo del volumen.",
      },
      { type: "h2", text: "Dos plantillas, dos trabajos distintos" },
      {
        type: "p",
        text: "Una página de categoría agrupa una variedad de productos que responden a una necesidad amplia ('zapatillas de running para mujer'). Una página de producto responde a una decisión ya tomada sobre un artículo específico ('zapatillas Nike Pegasus 41 talla 8'). Cuando ambas plantillas compiten por la misma consulta, ninguna termina posicionando bien.",
      },
      { type: "h2", text: "Comparativa: categoría vs producto" },
      {
        type: "table",
        head: ["Criterio", "Página de categoría", "Página de producto"],
        rows: [
          [
            "Tipo de intención",
            "Exploratoria / comparativa",
            "Transaccional específica",
          ],
          [
            "Competencia orgánica",
            "Suele ser mayor volumen de búsqueda",
            "Cola larga, más específica",
          ],
          [
            "Rol en la conversión",
            "Punto de entrada y filtrado",
            "Cierre de la decisión de compra",
          ],
          [
            "Necesidad de contenido",
            "Introducción de categoría, filtros útiles, enlazado interno",
            "Descripción única, atributos, disponibilidad, reseñas",
          ],
          [
            "Riesgo principal",
            "Canibalización si hay varias categorías casi idénticas",
            "Contenido duplicado si se usa la ficha del fabricante",
          ],
        ],
      },
      {
        type: "p",
        text: "Esta tabla no sustituye una revisión de la intención real detrás de cada término: sirve como punto de partida para decidir qué plantilla priorizar antes de invertir tiempo de redacción o desarrollo.",
      },
      { type: "h2", text: "Navegación facetada y presupuesto de rastreo" },
      {
        type: "p",
        text: "Los filtros de una página de categoría (talla, color, precio) generan combinaciones de URL que, si se indexan todas, multiplican el número de páginas casi idénticas que un motor de búsqueda debe rastrear. Google documenta cómo gestionar parámetros de URL y quo canonicalización para evitar este problema.",
      },
      {
        type: "callout",
        text: "Google recomienda usar rel=canonical, robots.txt o configuraciones de parámetros solo para las combinaciones de filtros sin demanda de búsqueda propia, y permitir la indexación de aquellas con volumen real y suficiente diferenciación de contenido.",
        source: {
          label: "Google Search Central: Canonicalización",
          href: "https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls",
        },
      },
      {
        type: "list",
        items: [
          "Identificar qué combinaciones de filtros tienen volumen de búsqueda propio antes de decidir si se indexan",
          "Usar canonical hacia la categoría base para combinaciones sin demanda propia",
          "Evitar bloquear con robots.txt páginas que ya generan enlaces o tráfico, ya que eso impide que Google procese el canonical",
          "Revisar periódicamente los logs de rastreo para confirmar que el bot no está gastando presupuesto en URLs de filtros irrelevantes",
        ],
      },
      { type: "h2", text: "Datos estructurados y variantes de producto" },
      {
        type: "p",
        text: "Cuando un producto tiene variantes (talla, color), la decisión de usar una URL por variante o una sola página con selector afecta tanto la experiencia como el marcado de datos estructurados. Google documenta el uso de Product y de la propiedad hasVariant para representar variantes dentro de una misma página o en páginas separadas enlazadas entre sí.",
      },
      {
        type: "callout",
        text: "El marcado de producto debe incluir precio, disponibilidad y, cuando exista, valoración agregada. Si hay variantes con precios o disponibilidad distintos, cada una debe reflejarse con su propio Offer.",
        source: {
          label: "Google Search Central: Product structured data",
          href: "https://developers.google.com/search/docs/appearance/structured-data/product",
        },
      },
      { type: "h2", text: "Un flujo para decidir qué priorizar" },
      {
        type: "p",
        text: "Antes de crear o rehacer una plantilla, este flujo ayuda a decidir dónde enfocar el trabajo primero:",
      },
      {
        type: "steps",
        items: [
          "Agrupar las consultas objetivo por intención: exploratoria (categoría) vs específica de un artículo (producto)",
          "Cruzar esa clasificación con el volumen de búsqueda y con las páginas de categoría o producto ya existentes",
          "Detectar canibalización: dos páginas propias compitiendo por la misma consulta",
          "Auditar la navegación facetada para separar combinaciones con demanda propia de las que no la tienen",
          "Priorizar contenido único en las páginas de producto con mayor tráfico potencial antes que en el resto del catálogo",
          "Verificar el marcado de datos estructurados en la plantilla ganadora antes de escalar el cambio a todo el catálogo",
        ],
      },
      {
        type: "p",
        text: "El objetivo no es tener una página por cada combinación posible, sino que cada página indexada tenga una razón clara de existir frente a una intención de búsqueda distinta.",
      },
    ],
    cta: {
      text: "Si tu catálogo tiene páginas de categoría y producto compitiendo entre sí, o miles de URLs generadas por filtros sin control, esto se puede auditar con datos concretos de rastreo e indexación.",
      label: "Ver el servicio de SEO para ecommerce",
      href: "/servicios/ecommerce-seo",
    },
    related: [
      {
        label: "Amazon SEO vs Amazon Ads: dónde entra el descubrimiento orgánico",
        href: "/biblioteca/amazon-seo-vs-amazon-ads",
      },
      { label: "Servicio de SEO para ecommerce", href: "/servicios/ecommerce-seo" },
      { label: "Consultoría de Amazon", href: "/servicios/amazon-consulting" },
    ],
  },
  en: {
    title: "Category vs Product Pages: What to Rank in Ecommerce",
    h1: "Category Pages vs Product Pages: Which One Should Rank for Which Query",
    description:
      "How to decide whether an ecommerce query should be answered by a category page or a product page, and how to stop them from competing.",
    eyebrow: "Ecommerce SEO",
    summary:
      "In a large catalog, not every query deserves its own product page: broad queries often convert better through a well-structured category page, while queries about one specific item need their own listing.",
    body: [
      {
        type: "p",
        text: "One of the most common ecommerce mistakes is assuming every keyword should be won with a new page. In practice, the right template depends on the intent behind the search, not just on volume.",
      },
      { type: "h2", text: "Two templates, two different jobs" },
      {
        type: "p",
        text: "A category page groups a range of products answering a broad need ('women's running shoes'). A product page answers a decision the shopper has already made about one specific item ('Nike Pegasus 41 size 8'). When both templates compete for the same query, neither ranks well.",
      },
      { type: "h2", text: "Comparison: category vs product" },
      {
        type: "table",
        head: ["Criterion", "Category page", "Product page"],
        rows: [
          ["Intent type", "Exploratory / comparative", "Specific transactional"],
          [
            "Organic competition",
            "Usually higher search volume",
            "Long-tail, more specific",
          ],
          [
            "Role in conversion",
            "Entry point and filtering",
            "Closes the purchase decision",
          ],
          [
            "Content needs",
            "Category intro, useful filters, internal linking",
            "Unique description, attributes, availability, reviews",
          ],
          [
            "Main risk",
            "Cannibalization from near-duplicate categories",
            "Duplicate content from manufacturer copy",
          ],
        ],
      },
      {
        type: "p",
        text: "This table doesn't replace a review of the actual intent behind each term: it's a starting point for deciding which template to prioritize before spending time on copy or development.",
      },
      { type: "h2", text: "Faceted navigation and crawl budget" },
      {
        type: "p",
        text: "Filters on a category page (size, color, price) generate URL combinations that, if all indexed, multiply the number of near-identical pages a search engine has to crawl. Google documents how to handle URL parameters and canonicalization to avoid this.",
      },
      {
        type: "callout",
        text: "Google recommends using rel=canonical, robots.txt, or parameter settings only for filter combinations with no search demand of their own, and allowing indexing for combinations with real volume and enough content differentiation.",
        source: {
          label: "Google Search Central: Canonicalization",
          href: "https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls",
        },
      },
      {
        type: "list",
        items: [
          "Identify which filter combinations have their own search volume before deciding whether to index them",
          "Use canonical tags pointing to the base category for combinations without their own demand",
          "Avoid blocking already-linked or trafficked pages with robots.txt, since that prevents Google from processing the canonical",
          "Review crawl logs periodically to confirm the bot isn't spending budget on irrelevant filter URLs",
        ],
      },
      { type: "h2", text: "Structured data and product variants" },
      {
        type: "p",
        text: "When a product has variants (size, color), deciding between one URL per variant or a single page with a selector affects both user experience and structured data markup. Google documents Product markup and the hasVariant property to represent variants either on the same page or on separate, linked pages.",
      },
      {
        type: "callout",
        text: "Product markup should include price, availability, and, where available, an aggregate rating. If variants have different prices or availability, each one should be reflected with its own Offer.",
        source: {
          label: "Google Search Central: Product structured data",
          href: "https://developers.google.com/search/docs/appearance/structured-data/product",
        },
      },
      { type: "h2", text: "A workflow for deciding what to prioritize" },
      {
        type: "p",
        text: "Before building or rebuilding a template, this workflow helps decide where to focus first:",
      },
      {
        type: "steps",
        items: [
          "Group target queries by intent: exploratory (category) vs specific to one item (product)",
          "Cross that classification with search volume and with existing category or product pages",
          "Spot cannibalization: two of your own pages competing for the same query",
          "Audit faceted navigation to separate combinations with real demand from those without it",
          "Prioritize unique content on the product pages with the highest potential traffic before the rest of the catalog",
          "Verify structured data markup on the winning template before rolling the change out to the whole catalog",
        ],
      },
      {
        type: "p",
        text: "The goal isn't a page for every possible combination, but making sure every indexed page has a clear reason to exist against a distinct search intent.",
      },
    ],
    cta: {
      text: "If your catalog has category and product pages competing against each other, or thousands of uncontrolled filter URLs, this can be audited with concrete crawl and indexation data.",
      label: "See the ecommerce SEO service",
      href: "/en/services/ecommerce-seo",
    },
    related: [
      {
        label: "Amazon SEO vs Amazon Advertising: Where Organic Discovery Fits",
        href: "/en/library/amazon-seo-vs-amazon-advertising",
      },
      { label: "Ecommerce SEO service", href: "/en/services/ecommerce-seo" },
      { label: "Amazon consulting", href: "/en/services/amazon-consulting" },
    ],
  },
};
